/**
 * Категории меню — /menu/categories (design manager v3 §9.2).
 *
 * Воспроизводит мокап 9.2 «Категории меню»:
 *   - drag-sortable список категорий (ручка ⋮⋮, перетаскивание меняет порядок);
 *   - у строки: cover-обложка 48×48 (placeholder с иконкой если нет фото),
 *     название + время доступности + счётчик позиций, статус-чип (активна/скрыта);
 *   - клик по строке → форма редактирования (CategoryForm);
 *   - «+ Категория» → форма создания.
 * Поля категории (миграция 0067): иконка, cover_url, availability — правятся
 * в CategoryForm. Drag-перестановка сохраняется через reorder-мутацию
 * (PUT sort_order по каждой затронутой категории).
 *
 * Мобайл 375: список full-width, строки компактные (как в дизайне).
 * i18n: 100% RU. Пусто: «Создайте первую категорию (например, Завтраки)».
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Skeleton } from '@hsc/ui';
import { localizeText } from '@hsc/types';
import { Toast } from '../../shared/ui/Toast.js';
import { photoUrl } from '../../lib/fleet-format.js';
import { CategoryForm } from './MenuManager.js';
import { CategoryIcon, categoryIconKind, type CategoryIconKind } from './guest-shared.js';
import type { AdminCategory } from './menu-admin.js';
import { useMenuAdmin, useMenuCategoryMutations, type MenuAdminCategory } from './useMenu.js';

const LOCALE = 'ru';

/** Сохранённый ключ иконки (пикер §9.2) → вид брендовой SVG-иконки. */
const ICON_KEY_TO_KIND: Record<string, CategoryIconKind> = {
  coffee: 'drinks',
  'fork-knife': 'kitchen',
  salad: 'kitchen',
  cake: 'dessert',
  wine: 'drinks',
  beer: 'drinks',
  baby: 'kids',
  leaf: 'kitchen',
};

/** Вид брендовой иконки категории: по сохранённому ключу, иначе по code/name. */
function categoryIconKindFor(cat: AdminCategory): CategoryIconKind {
  if (cat.icon !== undefined && cat.icon !== '' && ICON_KEY_TO_KIND[cat.icon] !== undefined) {
    return ICON_KEY_TO_KIND[cat.icon] as CategoryIconKind;
  }
  return categoryIconKind(cat.code, localizeText(cat.name, LOCALE) || cat.code);
}

/** Подпись доступности строки категории. */
function availabilityLabel(c: AdminCategory): string {
  return c.availability !== undefined && c.availability !== ''
    ? c.availability.replace('-', ' — ')
    : 'весь день';
}

export function MenuCategories(): JSX.Element {
  const navigate = useNavigate();
  const { data: categories, isLoading, isError, refetch } = useMenuAdmin();
  const { reorder, archiveMany } = useMenuCategoryMutations();
  const [editing, setEditing] = useState<{ category: AdminCategory | null } | null>(null);
  const [order, setOrder] = useState<MenuAdminCategory[]>([]);
  const [toast, setToast] = useState<{ msg: string; tone: 'info' | 'error' } | null>(null);
  // Режим выбора для bulk-архивации (§9.2, #74).
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const dragIdx = useRef<number | null>(null);
  const notify = (msg: string, tone: 'info' | 'error' = 'info'): void => setToast({ msg, tone });

  const toggleSelect = (id: string): void =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const clearSelection = (): void => setSelected(new Set());
  const archiveSelected = (): void => {
    // Архивируем только ещё активные из выбранных (deactivate идемпотентен,
    // но не шлём лишних запросов по уже скрытым).
    const ids = order.filter((c) => selected.has(c.id) && c.is_active).map((c) => c.id);
    if (ids.length === 0) {
      notify('Выбранные категории уже в архиве.', 'info');
      clearSelection();
      return;
    }
    archiveMany.mutate(ids, {
      onSuccess: () => {
        notify(`Архивировано категорий: ${ids.length}.`);
        clearSelection();
        void refetch();
      },
      onError: (e) => notify(e.message || 'Не удалось архивировать категории.', 'error'),
    });
  };

  // Локальный порядок синхронизируем с данными (для оптимистичного drag).
  useEffect(() => {
    if (categories !== undefined) setOrder(categories);
  }, [categories]);

  if (editing) {
    return (
      <CategoryForm
        category={editing.category}
        onClose={() => setEditing(null)}
        onDone={(m) => {
          setEditing(null);
          notify(m);
          void refetch();
        }}
        onError={(m) => notify(m, 'error')}
      />
    );
  }

  const onDrop = (toIdx: number): void => {
    const from = dragIdx.current;
    dragIdx.current = null;
    if (from === null || from === toIdx) return;
    const next = [...order];
    const [moved] = next.splice(from, 1);
    if (moved === undefined) return;
    next.splice(toIdx, 0, moved);
    setOrder(next);
    reorder.mutate(next, {
      onSuccess: () => notify('Порядок категорий сохранён.'),
      onError: (e) => notify(e.message || 'Не удалось сохранить порядок.', 'error'),
    });
  };

  return (
    <section data-testid="menu-categories">
      <button
        type="button"
        className="ta-back-btn"
        data-testid="menu-categories-back"
        onClick={() => navigate('/menu')}
      >
        <span className="ta-back-btn__chev" aria-hidden="true">
          ‹
        </span>{' '}
        Назад
      </button>
      <div className="ta-topbar">
        <div>
          <div className="ta-crumbs">Меню / Категории</div>
          <h1 className="hsc-page__title">Категории · {order.length}</h1>
        </div>
        <Button
          variant="primary"
          size="sm"
          data-testid="menu-cat-add"
          onClick={() => setEditing({ category: null })}
        >
          + Категория
        </Button>
      </div>

      {/* Bulk-панель архивации (§9.2, #74) — появляется при выборе строк. */}
      {selected.size > 0 && (
        <div className="mi-bulk" data-testid="menu-cat-bulk-bar">
          <span className="mi-bulk__count" data-testid="menu-cat-bulk-count">
            Выбрано: {selected.size}
          </span>
          <Button
            variant="ghost"
            size="sm"
            disabled={archiveMany.isPending}
            data-testid="menu-cat-bulk-archive"
            onClick={archiveSelected}
          >
            Архивировать выбранные
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={archiveMany.isPending}
            data-testid="menu-cat-bulk-clear"
            onClick={clearSelection}
          >
            Снять выбор
          </Button>
        </div>
      )}

      {isLoading ? (
        <div data-testid="menu-categories-skeleton">
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={i} height={64} radius={12} />
          ))}
        </div>
      ) : isError || categories === undefined ? (
        <Card>
          <p role="alert" data-testid="menu-categories-error">
            Не удалось загрузить категории. Попробуйте обновить страницу.
          </p>
          <div className="rq-actions">
            <Button variant="secondary" onClick={() => void refetch()}>
              Повторить
            </Button>
          </div>
        </Card>
      ) : order.length === 0 ? (
        <Card>
          <p data-testid="menu-categories-empty">
            Создайте первую категорию (например, «Завтраки»).
          </p>
        </Card>
      ) : (
        <ul className="menu-cat-list" data-testid="menu-categories-list">
          {order.map((cat, idx) => {
            const cover =
              cat.cover_url !== undefined && cat.cover_url !== ''
                ? photoUrl(cat.cover_url) ?? cat.cover_url
                : null;
            return (
              // Drag-сортировка (design §9.2) — pointer-native; клавиатурная
              // альтернатива не требуется (порядок — улучшение, основной CRUD
              // доступен кликом по строке). Глушим a11y-правило для DnD.
              // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
              <li
                key={cat.id}
                className="menu-cat-row"
                data-testid={`menu-cat-row-${cat.code}`}
                draggable
                onDragStart={() => {
                  dragIdx.current = idx;
                }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => onDrop(idx)}
              >
                <span className="menu-cat-row__select">
                  <input
                    type="checkbox"
                    aria-label={`Выбрать «${localizeText(cat.name, LOCALE) || cat.code}»`}
                    checked={selected.has(cat.id)}
                    data-testid={`menu-cat-select-${cat.code}`}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => toggleSelect(cat.id)}
                  />
                </span>
                <span className="menu-cat-row__handle" aria-hidden="true" title="Перетащите для сортировки">
                  ⋮⋮
                </span>
                <span className="menu-cat-row__cover" aria-hidden="true">
                  {cover !== null ? (
                    <img src={cover} alt="" className="menu-cat-row__cover-img" />
                  ) : (
                    <span className="menu-cat-row__cover-ph">
                      <CategoryIcon kind={categoryIconKindFor(cat)} className="menu-cat-row__cover-icon" />
                    </span>
                  )}
                </span>
                <button
                  type="button"
                  className="menu-cat-row__main"
                  data-testid={`menu-cat-edit-${cat.code}`}
                  onClick={() => setEditing({ category: cat })}
                >
                  <strong>{localizeText(cat.name, LOCALE) || cat.code}</strong>
                  <span className="menu-cat-row__meta">
                    {availabilityLabel(cat)} · {cat.items.length} поз.
                  </span>
                </button>
                <span className={`chip${cat.is_active ? ' chip--ok' : ''}`}>
                  {cat.is_active ? 'активна' : 'скрыта'}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {toast && <Toast message={toast.msg} tone={toast.tone} onClose={() => setToast(null)} />}
    </section>
  );
}

export default MenuCategories;
